import { booleanAttribute, Component, computed, input } from '@angular/core';
import { MibPalette } from '../../foundations';
import { MibIconComponent } from '../icons';
import { AvatarMode, AvatarSize } from './avatar.models';

/**
 * Metrics of the round, one row per step: its side, the size of the letters inside it, and the icon
 * that stands in when there are none.
 *
 * The icon is set on **both axes** and lands on the icon scale at every step — an `h-*` alone would
 * leave the 24px width of the `size-6` default in place, which in a 24px round is wider than the
 * round itself. It sits at half the diameter throughout, so a lettered and an icon avatar read at
 * the same weight side by side.
 */
const METRICS: Record<AvatarSize, { round: string; icon: string }> = {
  xs: { round: 'size-6 text-xs', icon: 'size-3' },
  sm: { round: 'size-8 text-xs font-semibold', icon: 'size-4' },
  md: { round: 'size-10 text-base', icon: 'size-5' },
  lg: { round: 'size-12 text-xl', icon: 'size-6' },
  xl: { round: 'size-14 text-xl', icon: 'size-8' },
};

/** The branding pair each mode paints the round with. */
const MODES: Record<AvatarMode, string> = {
  filled: 'bg-mib-branding-900 text-white',
  subtle: 'bg-mib-branding-50 text-mib-branding-900',
};

/**
 * The grounds {@link MibAvatarComponent.randomColor} draws from: every palette at 700, 800 and 900,
 * the steps white initials hold AA on. Written out because Tailwind only generates the classes it
 * finds in the source.
 */
/* eslint-disable mib/no-palette-colour -- avatar palettes: data, not interface colours */
const RANDOM_GROUNDS: Record<MibPalette, readonly string[]> = {
  navy: ['bg-mib-branding-700', 'bg-mib-branding-800', 'bg-mib-branding-900'],
  indigo: ['bg-mib-indigo-700', 'bg-mib-indigo-800', 'bg-mib-indigo-900'],
  grey: ['bg-gray-700', 'bg-gray-800', 'bg-gray-900'],
  green: ['bg-green-700', 'bg-green-800', 'bg-green-900'],
  orange: ['bg-orange-700', 'bg-orange-800', 'bg-orange-900'],
  red: ['bg-red-700', 'bg-red-800', 'bg-red-900'],
  cyan: ['bg-cyan-700', 'bg-cyan-800', 'bg-cyan-900'],
  sky: ['bg-sky-700', 'bg-sky-800', 'bg-sky-900'],
  violet: ['bg-violet-700', 'bg-violet-800', 'bg-violet-900'],
  pink: ['bg-pink-700', 'bg-pink-800', 'bg-pink-900'],
};
/* eslint-enable mib/no-palette-colour */
const ALL_RANDOM_GROUNDS = Object.values(RANDOM_GROUNDS).flat();

/** A non-negative hash of a string, the same on every run, to turn a key into a colour. */
function hashOf(key: string): number {
  let hash = 0;
  for (const char of key) {
    hash = (hash * 31 + (char.codePointAt(0) ?? 0)) >>> 0;
  }
  return hash;
}

/**
 * A round carrying someone's initials, or the user icon when there is no name to take them from.
 *
 * ```html
 * <mib-avatar username="Dr. Martin" size="md" />
 * <mib-avatar username="Dr. Martin" mode="subtle" withName description="Cardiologist" />
 * <mib-avatar username="Dr. Martin" withName nameFirst />
 * ```
 *
 * The round is drawn inside the component, so a class written on `<mib-avatar>` does not reach it.
 * Its colours are a closed set instead: {@link mode}, or {@link randomColor}.
 */
@Component({
  selector: 'mib-avatar',
  imports: [MibIconComponent],
  templateUrl: './avatar.component.html',
  host: {
    /*
     The row lives on the host rather than in a wrapper inside it, so the name block can be given a
     width from the call site — `w-fit` on a wrapper meant `withName` could never fill a panel and
     truncate. `inline-flex` still sizes a bare round to its own diameter.
    */
    class: 'inline-flex items-center gap-3',
    '[class.flex-row-reverse]': 'nameFirst()',
    /*
     A bare avatar is exactly its round, so the host declares the shape: `[mibBareButton]` reads it
     with `:has(> .rounded-full)` to take the same corner. With the name beside it the box is a row,
     and the declaration goes away with the shape.
    */
    '[class.rounded-full]': '!withName()',
  },
})
export class MibAvatarComponent {
  /** Name the initials are taken from. Without it the round shows the user icon. */
  readonly username = input<string>();
  /** Line under the name. Only rendered with {@link withName}. */
  readonly description = input<string>();
  /** Diameter of the round, 24 / 32 / 40 / 48 / 56px. */
  readonly size = input<AvatarSize>('sm');
  /** How much of the branding colour the round carries. */
  readonly mode = input<AvatarMode>('filled');
  /** Draws the name, and the description under it, beside the round. */
  readonly withName = input(false, { transform: booleanAttribute });
  /**
   * Puts the name before the round instead of after it, and aligns it to the right.
   *
   * The two travel together: a block whose name comes first is one anchored to a right edge — the end
   * of an application bar — where ragged-left text would drift away from the round it belongs to.
   */
  readonly nameFirst = input(false, { transform: booleanAttribute });
  /** The letters to draw in place of the ones taken from {@link username}, two at most — for a name led by a title. */
  readonly customInitials = input<string>();
  /**
   * Paints the round in a colour drawn at random, once per avatar, from every palette at 700, 800 or
   * 900, with white initials, in place of {@link mode}'s. The same person may get another colour elsewhere.
   */
  readonly randomColor = input(false, { transform: booleanAttribute });
  /** With {@link randomColor}, derives the colour from this key instead of drawing it: every avatar of one key gets the same colour. */
  readonly colorKey = input<string>();
  /** Draws `+N` in the round in place of initials, for the people a stack leaves out: a whole number, `+99` above 99. */
  readonly count = input<number>();

  private readonly drawnGround = ALL_RANDOM_GROUNDS[Math.floor(Math.random() * ALL_RANDOM_GROUNDS.length)];
  private readonly randomGround = computed(() => {
    const key = this.colorKey();
    return key ? ALL_RANDOM_GROUNDS[hashOf(key) % ALL_RANDOM_GROUNDS.length] : this.drawnGround;
  });

  /** The letters drawn in the round: `+N` from {@link count}, {@link customInitials}, or the first of each word of {@link username}, two at most. */
  readonly initials = computed(() => {
    const count = this.count();
    if (count !== undefined && count >= 1) return `+${Math.min(Math.floor(count), 99)}`;
    return (
      this.customInitials()?.trim().substring(0, 2) ||
      this.username()
        ?.trim()
        ?.split(' ')
        .map((word) => word.substring(0, 1))
        .join('')
        .substring(0, 2)
    );
  });

  protected readonly displayIcon = computed(() => !this.initials());

  protected readonly roundClasses = computed(() => {
    const colour = this.randomColor() ? `${this.randomGround()} text-white` : MODES[this.mode()];
    return `${colour} ${METRICS[this.size()].round}`;
  });
  protected readonly iconClasses = computed(() => METRICS[this.size()].icon);
}
